package fr.manyverse.app.presentation.theme

import androidx.compose.material3.ColorScheme
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import com.materialkolor.PaletteStyle
import com.materialkolor.rememberDynamicColorScheme
import fr.manyverse.app.domain.model.UniverseConfig

object UniverseColorScheme {

    // Default - palette de repli neutre (rouge/orange) pour univers custom et univers non résolus
    val DefaultLightColorScheme = lightColorScheme(
        primary = Color(0xFFB23A2E),
        onPrimary = Color(0xFFFFFFFF),
        primaryContainer = Color(0xFFFFDAD5),
        onPrimaryContainer = Color(0xFF8C1D13),
        secondary = Color(0xFFD97A0B),
        onSecondary = Color(0xFFFFFFFF),
        secondaryContainer = Color(0xFFFFDDB3),
        onSecondaryContainer = Color(0xFFAB5D00),
        tertiary = Color(0xFF8B6914),
        onTertiary = Color(0xFFFFFFFF),
        tertiaryContainer = Color(0xFFFFDF9E),
        onTertiaryContainer = Color(0xFF6B4F00),
        error = Color(0xFFBA1A1A),
        onError = Color(0xFFFFFFFF),
        errorContainer = Color(0xFFFFDAD6),
        onErrorContainer = Color(0xFF93000A),
        background = Color(0xFFFFF8F6),
        onBackground = Color(0xFF231917),
        surface = Color(0xFFFFF8F6),
        onSurface = Color(0xFF231917),
        surfaceVariant = Color(0xFFF5DDD9),
        onSurfaceVariant = Color(0xFF534340),
        outline = Color(0xFF857370),
        outlineVariant = Color(0xFFD8C2BD),
        scrim = Color(0xFF000000),
        inverseSurface = Color(0xFF392E2B),
        inverseOnSurface = Color(0xFFFFEDEA),
        inversePrimary = Color(0xFFFFB4A9),
        surfaceDim = Color(0xFFE8D6D2),
        surfaceBright = Color(0xFFFFF8F6),
        surfaceContainerLowest = Color(0xFFFFFFFF),
        surfaceContainerLow = Color(0xFFFFF0EE),
        surfaceContainer = Color(0xFFFCEAE6),
        surfaceContainerHigh = Color(0xFFF6E4E0),
        surfaceContainerHighest = Color(0xFFF0DEDA),
    )

    val DefaultDarkColorScheme = darkColorScheme(
        primary = Color(0xFFFFB4A9),
        onPrimary = Color(0xFF690003),
        primaryContainer = Color(0xFF93000A),
        onPrimaryContainer = Color(0xFFFFDAD5),
        secondary = Color(0xFFFFB74D),
        onSecondary = Color(0xFF5B3000),
        secondaryContainer = Color(0xFF824600),
        onSecondaryContainer = Color(0xFFFFDDB3),
        tertiary = Color(0xFFE5C253),
        onTertiary = Color(0xFF3C2F00),
        tertiaryContainer = Color(0xFF574500),
        onTertiaryContainer = Color(0xFFFFDF9E),
        error = Color(0xFFFFB4AB),
        onError = Color(0xFF690005),
        errorContainer = Color(0xFF93000A),
        onErrorContainer = Color(0xFFFFDAD6),
        background = Color(0xFF1A110F),
        onBackground = Color(0xFFF0DEDA),
        surface = Color(0xFF1A110F),
        onSurface = Color(0xFFF0DEDA),
        surfaceVariant = Color(0xFF534340),
        onSurfaceVariant = Color(0xFFD8C2BD),
        outline = Color(0xFFA08C88),
        outlineVariant = Color(0xFF534340),
        scrim = Color(0xFF000000),
        inverseSurface = Color(0xFFF0DEDA),
        inverseOnSurface = Color(0xFF392E2B),
        inversePrimary = Color(0xFFB23A2E),
        surfaceDim = Color(0xFF1A110F),
        surfaceBright = Color(0xFF423734),
        surfaceContainerLowest = Color(0xFF140C0A),
        surfaceContainerLow = Color(0xFF231917),
        surfaceContainer = Color(0xFF271D1B),
        surfaceContainerHigh = Color(0xFF322825),
        surfaceContainerHighest = Color(0xFF3D3230),
    )

    @Suppress("UNUSED_PARAMETER")
    fun getColorScheme(themeId: String, isDark: Boolean): ColorScheme {
        return if (isDark) DefaultDarkColorScheme else DefaultLightColorScheme
    }
}

/**
 * Resolves the effective [ColorScheme] for a universe: when a custom [UniverseConfig.themeSeedColor]
 * is set, a full Material 3 palette (light + dark) is generated from it via MaterialKolor
 * ([PaletteStyle.Fidelity] keeps the chosen color faithfully visible). Otherwise falls back to the
 * hand-crafted built-in palettes ([UniverseColorScheme.getColorScheme]).
 */
@Composable
fun rememberUniverseColorScheme(config: UniverseConfig, isDark: Boolean): ColorScheme {
    val seed = config.themeSeedColor
    return if (seed != null) {
        rememberDynamicColorScheme(
            seedColor = Color(seed),
            isDark = isDark,
            style = PaletteStyle.Fidelity
        )
    } else {
        UniverseColorScheme.getColorScheme(config.themeId, isDark)
    }
}
